import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import JSZip from 'jszip';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { LocalDatabaseService } from '../storage/local-database.service';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { getBinaryNative, getJsonNative, postJsonNative } from '../platform/native-http';
import { needsNativeDiscoveryFetch } from '../../oidc-discovery.loader';
import { DeviceIdService } from '../device/device-id.service';
import { LoggerService } from '../utils/logger.service';
import { isSupportedContentPackSchema } from './content-pack.types';
import type {
  ContentKeyResponse,
  ContentPackDownloadPayload,
  ContentPackCheckResponse,
  ContentPackListResponse,
  ContentPackManifest,
} from './content-pack.types';

const META_KEY_PREFIX = 'content_pack_version_';
const AUTO_DOWNLOAD_KEY = 'content_pack_auto_download';
const LAST_RELEASE_CHECK_PREFIX = 'content_pack_last_release_check_';

/**
 * Per-subject offline pack lifecycle (PR5 / 3c): list manifests, check updates, request keys.
 * Encrypted blob download / SQLCipher import is a follow-up PR.
 */
@Injectable({ providedIn: 'root' })
export class ContentPackService {
  constructor(
    private http: HttpClient,
    private runtime: RuntimeConfigService,
    private connectivity: ConnectivityService,
    private localDb: LocalDatabaseService,
    private deviceId: DeviceIdService
  ) {}

  private get apiUrl(): string {
    return this.runtime.apiBaseUrl;
  }

  private authHeaderRecord(): Record<string, string> | null {
    const headers = this.sessionHeaders();
    if (!headers) {
      return null;
    }
    return {
      Authorization: headers.get('Authorization') ?? '',
      [this.deviceId.headerName()]: this.deviceId.getId(),
    };
  }

  private useNativeHttp(url: string): boolean {
    return isCapacitorNative() || needsNativeDiscoveryFetch(url);
  }

  private sessionHeaders(): HttpHeaders | null {
    let sessionId: string | null = null;
    try {
      const raw = localStorage.getItem('user');
      sessionId = raw ? (JSON.parse(raw)?._sid ?? null) : null;
    } catch {
      sessionId = null;
    }
    if (!sessionId) {
      return null;
    }
    return new HttpHeaders({
      Authorization: `Bearer ${sessionId}`,
      [this.deviceId.headerName()]: this.deviceId.getId(),
    });
  }

  private metaKey(courseId: number, subjectId: number): string {
    return `${META_KEY_PREFIX}${courseId}_${subjectId}`;
  }

  getLocalVersion(courseId: number, subjectId: number): string | null {
    return localStorage.getItem(this.metaKey(courseId, subjectId));
  }

  setLocalVersion(courseId: number, subjectId: number, version: string): void {
    localStorage.setItem(this.metaKey(courseId, subjectId), version);
  }

  isAutoDownloadEnabled(): boolean {
    return localStorage.getItem(AUTO_DOWNLOAD_KEY) !== 'false';
  }

  setAutoDownloadEnabled(enabled: boolean): void {
    localStorage.setItem(AUTO_DOWNLOAD_KEY, enabled ? 'true' : 'false');
  }

  async clearDownloadedPacks(): Promise<void> {
    if (!isCapacitorNative()) {
      return;
    }
    await this.localDb.init();
    await this.localDb.clearDownloadedPacks();
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(META_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  }

  async listDownloadedPackStats(): Promise<
    { courseId: number; subjectId: number; questionCount: number; payloadBytes: number }[]
  > {
    if (!isCapacitorNative()) {
      return [];
    }
    await this.localDb.init();
    return this.localDb.listDownloadedPackStats();
  }

  async listPackStatuses(): Promise<
    {
      courseId: number;
      subjectId: number;
      contentVersion: string | null;
      state: 'pending' | 'downloading' | 'importing' | 'installed' | 'failed' | 'unknown';
      attemptCount: number;
      lastError: string | null;
      questionCount: number;
      payloadBytes: number;
      hasManifest: boolean;
      updatedAt: string | null;
    }[]
  > {
    if (!isCapacitorNative()) {
      return [];
    }
    await this.localDb.init();
    return this.localDb.listPackStatuses();
  }

  async listSubjectPacks(courseId: number): Promise<ContentPackListResponse | null> {
    const auth = this.authHeaderRecord();
    if (!auth) {
      return null;
    }
    const url = `${this.apiUrl}/content-packs?courseId=${courseId}`;
    try {
      if (this.useNativeHttp(url)) {
        return await getJsonNative<ContentPackListResponse>(url, auth);
      }
      return await firstValueFrom(
        this.http.get<ContentPackListResponse>(url, { headers: this.sessionHeaders()! })
      );
    } catch (e) {
      LoggerService.error('[ContentPackService] list failed', e);
      return null;
    }
  }

  async checkForUpdates(
    courseId: number,
    subjectId: number
  ): Promise<ContentPackCheckResponse | null> {
    const auth = this.authHeaderRecord();
    if (!auth) {
      return null;
    }
    const localVersion = this.getLocalVersion(courseId, subjectId) ?? '';
    const params = new URLSearchParams({
      courseId: String(courseId),
      subjectId: String(subjectId),
    });
    if (localVersion) {
      params.set('localVersion', localVersion);
    }
    try {
      const url = `${this.apiUrl}/content-packs/check?${params.toString()}`;
      if (this.useNativeHttp(url)) {
        return await getJsonNative<ContentPackCheckResponse>(url, auth);
      }
      return await firstValueFrom(
        this.http.get<ContentPackCheckResponse>(url, { headers: this.sessionHeaders()! })
      );
    } catch (e) {
      LoggerService.error('[ContentPackService] check failed', e);
      return null;
    }
  }

  async requestContentKey(
    courseId: number,
    subjectId: number,
    packVersion?: string
  ): Promise<ContentKeyResponse | null> {
    const auth = this.authHeaderRecord();
    if (!auth) {
      return null;
    }
    const url = `${this.apiUrl}/content-keys`;
    const body = { courseId, subjectId, packVersion: packVersion ?? undefined };
    try {
      if (this.useNativeHttp(url)) {
        return await postJsonNative<ContentKeyResponse>(url, body, auth);
      }
      return await firstValueFrom(
        this.http.post<ContentKeyResponse>(url, body, { headers: this.sessionHeaders()! })
      );
    } catch (e) {
      LoggerService.error('[ContentPackService] content-keys failed', e);
      return null;
    }
  }

  async downloadSubjectPack(
    courseId: number,
    subjectId: number,
    contentKey: string,
    expectedManifest: ContentPackManifest,
    downloadUrl?: string
  ): Promise<ContentPackDownloadPayload> {
    const auth = this.authHeaderRecord();
    if (!auth) {
      throw new Error('No session for pack download');
    }
    const url = this.resolvePackDownloadUrl(expectedManifest, courseId, subjectId, downloadUrl);
    let encryptionHeader: string | null;
    let nonce: string | null;
    let body: ArrayBuffer;
    try {
      if (this.useNativeHttp(url)) {
        const native = await getBinaryNative(url, auth);
        body = native.body;
        encryptionHeader =
          native.responseHeaders['x-atplane-pack-encryption']
          ?? native.responseHeaders['x-qtrainer-pack-encryption']
          ?? null;
        nonce =
          native.responseHeaders['x-atplane-pack-nonce']
          ?? native.responseHeaders['x-qtrainer-pack-nonce']
          ?? null;
      } else {
        const response = await firstValueFrom(
          this.http.get(url, { headers: this.sessionHeaders()!, responseType: 'arraybuffer', observe: 'response' })
        );
        encryptionHeader =
          response.headers.get('X-Atplane-Pack-Encryption')
          ?? response.headers.get('X-QTrainer-Pack-Encryption');
        nonce =
          response.headers.get('X-Atplane-Pack-Nonce')
          ?? response.headers.get('X-QTrainer-Pack-Nonce');
        const rawBody = response.body;
        if (!rawBody) {
          throw new Error('Download returned empty body');
        }
        body = this.coerceArrayBuffer(rawBody);
      }
    } catch (e) {
      LoggerService.error('[ContentPackService] download HTTP failed', e);
      const status = (e as { status?: number })?.status;
      throw new Error(`Download HTTP failed${status ? ` (status ${status})` : ''}: ${url}`);
    }
    if (body.byteLength === 0) {
      throw new Error('Download returned 0 bytes');
    }
    let zipBytes: ArrayBuffer;
    if (encryptionHeader === 'AES-256-GCM') {
      if (!nonce) {
        throw new Error('Encryption header set but pack nonce header missing');
      }
      try {
        zipBytes = await this.decryptPack(body, contentKey, nonce);
      } catch (e) {
        LoggerService.error('[ContentPackService] decrypt failed', e);
        throw new Error(`Pack decryption failed: ${(e as Error)?.message ?? e}`);
      }
    } else {
      zipBytes = body;
    }
    try {
      return await this.readQpackZip(zipBytes, expectedManifest);
    } catch (e) {
      LoggerService.error('[ContentPackService] zip parse failed', e);
      throw new Error(`Pack archive parse failed: ${(e as Error)?.message ?? e}`);
    }
  }

  private coerceArrayBuffer(body: unknown): ArrayBuffer {
    if (body instanceof ArrayBuffer) {
      return body;
    }
    if (ArrayBuffer.isView(body)) {
      const view = body as ArrayBufferView;
      return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
    }
    if (typeof body === 'string') {
      const looksBase64 = /^[A-Za-z0-9+/=\s]+$/.test(body);
      if (looksBase64) {
        try {
          const binary = atob(body.replace(/\s+/g, ''));
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          return bytes.buffer;
        } catch (e) {
          throw new Error(`Body is non-binary string and not base64: ${(e as Error)?.message}`);
        }
      }
      throw new Error('Body is a string and not base64 — CapacitorHttp may have decoded the response');
    }
    throw new Error(`Body has unexpected type: ${Object.prototype.toString.call(body)}`);
  }

  async hasLocalSubjectPack(courseId: number, subjectId: number): Promise<boolean> {
    if (!isCapacitorNative()) {
      return false;
    }
    await this.localDb.init();
    return this.localDb.hasInstalledPack(courseId, subjectId);
  }

  async retrySubjectPack(courseId: number, subjectId: number): Promise<boolean> {
    if (!isCapacitorNative()) {
      return false;
    }
    await this.localDb.init();
    await this.localDb.clearSubjectPackDownloadState(courseId, subjectId);
    return this.ensureSubjectPack(courseId, subjectId, { forceWhenAutoDisabled: true });
  }

  async ensureSubjectPack(
    courseId: number,
    subjectId: number,
    opts?: { forceWhenAutoDisabled?: boolean }
  ): Promise<boolean> {
    if (!isCapacitorNative()) {
      return false;
    }
    if (!opts?.forceWhenAutoDisabled && !this.isAutoDownloadEnabled()) {
      return false;
    }
    await this.localDb.init();
    if (await this.localDb.hasInstalledPack(courseId, subjectId)) {
      return true;
    }
    if (!this.connectivity.isOnline) {
      return false;
    }

    const check = await this.checkForUpdates(courseId, subjectId);
    const manifest = check?.manifest;
    if (!manifest || !isSupportedContentPackSchema(manifest)) {
      return false;
    }

    try {
      await this.localDb.upsertPackDownloadState(
        manifest.packId,
        courseId,
        subjectId,
        manifest.contentVersion,
        'downloading'
      );
      const key = await this.requestContentKey(courseId, subjectId, manifest.contentVersion);
      if (!key?.key) {
        throw new Error('Pack key unavailable');
      }
      const payload = await this.downloadSubjectPack(courseId, subjectId, key.key, manifest, key.downloadUrl);
      if (!payload?.manifest) {
        throw new Error('Pack archive missing manifest after download');
      }
      if (!Array.isArray(payload.questions) || payload.questions.length === 0) {
        throw new Error(`Pack archive contained ${Array.isArray(payload.questions) ? '0 questions' : 'no questions array'}`);
      }
      if (!isSupportedContentPackSchema(payload.manifest)) {
        throw new Error(`Unsupported content pack schema: version=${payload.manifest.packSchemaVersion}, hash=${payload.manifest.packSchemaHash}`);
      }
      await this.localDb.upsertPackDownloadState(
        payload.manifest.packId,
        payload.manifest.courseId,
        payload.manifest.subjectId,
        payload.manifest.contentVersion,
        'importing'
      );
      await this.localDb.importContentPack(payload.manifest, payload.questionIds ?? [], payload.questions);
      await this.localDb.upsertPackDownloadState(
        payload.manifest.packId,
        payload.manifest.courseId,
        payload.manifest.subjectId,
        payload.manifest.contentVersion,
        'installed',
        { resetAttempts: true }
      );
      this.setLocalVersion(payload.manifest.courseId, payload.manifest.subjectId, payload.manifest.contentVersion);
      return true;
    } catch (e) {
      LoggerService.error('[ContentPackService] import failed, cleaning partial pack', e);
      await this.localDb.clearSubjectPack(courseId, subjectId);
      await this.localDb.markPackDownloadFailed(
        manifest.packId,
        courseId,
        subjectId,
        manifest.contentVersion,
        e instanceof Error ? e.message : String(e)
      );
      return false;
    }
  }

  private resolvePackDownloadUrl(manifest: ContentPackManifest, courseId: number, subjectId: number, overrideUrl?: string): string {
    const downloadUrl = overrideUrl ?? (manifest as ContentPackManifest & { downloadUrl?: string }).downloadUrl;
    const fallback = `/api/content-packs/download-file?courseId=${courseId}&subjectId=${subjectId}`;
    const value = downloadUrl && downloadUrl.trim() ? downloadUrl : fallback;
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
    const api = new URL(this.apiUrl);
    if (value.startsWith('/api/')) {
      return `${api.origin}${value}`;
    }
    if (value.startsWith('/')) {
      return `${api.origin}${value}`;
    }
    return `${this.apiUrl.replace(/\/+$/, '')}/${value}`;
  }

  private async decryptPack(encrypted: ArrayBuffer, keyBase64: string, nonceBase64: string): Promise<ArrayBuffer> {
    const keyBytes = this.base64ToBytes(keyBase64);
    const nonce = this.base64ToBytes(nonceBase64);
    const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['decrypt']);
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, encrypted);
  }

  private async readQpackZip(bytes: ArrayBuffer, expectedManifest: ContentPackManifest): Promise<ContentPackDownloadPayload> {
    const zip = await JSZip.loadAsync(bytes);
    const manifestJson = await zip.file('manifest.json')?.async('string');
    const contentJson = await zip.file('content.json')?.async('string');
    if (!manifestJson || !contentJson) {
      throw new Error('Pack archive missing manifest.json or content.json');
    }
    const archiveManifest = JSON.parse(manifestJson);
    const content = JSON.parse(contentJson);
    const imagePaths = new Map<number, string>();
    for (const image of content.images ?? []) {
      if (image?.image_id != null && image?.output_path) {
        imagePaths.set(Number(image.image_id), String(image.output_path));
      }
    }

    const questions = [];
    for (const row of content.questions ?? []) {
      const imageId = this.firstImageId(row.question_image_ids);
      const imagePath = imageId != null ? imagePaths.get(imageId) : null;
      const imageFile = imagePath ? zip.file(imagePath) : null;
      const image = imageFile
        ? `data:image/webp;base64,${await imageFile.async('base64')}`
        : null;
      const answers = [row.answer_1, row.answer_2, row.answer_3, row.answer_4]
        .map((label, index) => ({ label, index }))
        .filter((answer) => answer.label != null)
        .map((answer) => {
          const letter = String.fromCharCode('A'.charCodeAt(0) + answer.index);
          const isCorrect = String(row.correct_answer ?? '').toUpperCase() === letter;
          return {
            option: `answer_${answer.index}`,
            label: String(answer.label),
            value: isCorrect ? answer.index + 1 : 0,
            isCorrect,
            isChecked: false,
            isDisabled: false,
            tip: null,
          };
        });
      questions.push({
        id: Number(row.id),
        title: String(row.question_html ?? ''),
        tip: row.explanation_html != null ? String(row.explanation_html) : null,
        image,
        answers,
      });
    }

    return {
      manifest: {
        ...expectedManifest,
        packId: archiveManifest.packId ?? expectedManifest.packId,
        courseId: Number(archiveManifest.courseId ?? expectedManifest.courseId),
        subjectId: Number(archiveManifest.subjectId ?? expectedManifest.subjectId),
        subjectName: archiveManifest.subjectName ?? expectedManifest.subjectName,
        seedIds: archiveManifest.seedIds ?? expectedManifest.seedIds,
        packSchemaVersion: Number(archiveManifest.packSchemaVersion ?? expectedManifest.packSchemaVersion),
        packSchemaHash: archiveManifest.packSchemaHash ?? expectedManifest.packSchemaHash,
        questionCount: Number(archiveManifest.questionCount ?? expectedManifest.questionCount),
      },
      questionIds: questions.map((question) => question.id),
      questions,
    };
  }

  private firstImageId(value: unknown): number | null {
    if (value == null) return null;
    const match = String(value).match(/\d+/);
    return match ? Number(match[0]) : null;
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  async dailyReleaseCheck(courseId: number): Promise<void> {
    if (!isCapacitorNative() || !this.connectivity.isOnline) {
      return;
    }
    const key = `${LAST_RELEASE_CHECK_PREFIX}${courseId}`;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(key) === today) {
      return;
    }
    const list = await this.listSubjectPacks(courseId);
    if (!list?.packs?.length) {
      return;
    }
    for (const pack of list.packs) {
      const installed = await this.localDb.getPackManifest(courseId, pack.subjectId);
      if (!installed) {
        continue;
      }
      const check = await this.checkForUpdates(courseId, pack.subjectId);
      if (check?.updateAvailable) {
        localStorage.setItem(`content_pack_update_available_${courseId}_${pack.subjectId}`, check.manifest.contentVersion);
      }
    }
    localStorage.setItem(key, today);
  }

  /**
   * Records manifest metadata locally after a successful check/key (blob import TBD).
   */
  async recordManifestInstalled(manifest: ContentPackManifest): Promise<void> {
    if (!isSupportedContentPackSchema(manifest)) {
      throw new Error(
        `Unsupported content pack schema: version=${manifest.packSchemaVersion}, hash=${manifest.packSchemaHash}`
      );
    }
    this.setLocalVersion(manifest.courseId, manifest.subjectId, manifest.contentVersion);
    if (isCapacitorNative()) {
      await this.localDb.init();
      await this.localDb.upsertPackManifest(
        manifest.packId,
        manifest.courseId,
        manifest.subjectId,
        manifest.contentVersion,
        manifest.packSchemaVersion,
        manifest.packSchemaHash,
        manifest.questionCount
      );
    }
  }

  /**
   * On native + online: check each entitled subject pack for updates (no download yet).
   */
  async refreshSubjectsIfNeeded(courseId: number, subjectIds: number[]): Promise<void> {
    if (!isCapacitorNative() || !this.connectivity.isOnline || subjectIds.length === 0) {
      return;
    }
    await this.dailyReleaseCheck(courseId);
    if (!this.isAutoDownloadEnabled()) {
      return;
    }
    for (const subjectId of subjectIds) {
      if (!(await this.localDb.hasInstalledPack(courseId, subjectId))) {
        await this.ensureSubjectPack(courseId, subjectId);
      }
    }
  }

  /** @deprecated Use refreshSubjectsIfNeeded — checks all packs from list API. */
  async refreshIfNeeded(courseIds: number[]): Promise<void> {
    if (!isCapacitorNative() || !this.connectivity.isOnline) {
      return;
    }
    for (const courseId of courseIds) {
      const list = await this.listSubjectPacks(courseId);
      if (!list?.packs?.length) {
        continue;
      }
      await this.dailyReleaseCheck(courseId);
      if (!this.isAutoDownloadEnabled()) {
        continue;
      }
      for (const pack of list.packs) {
        if (!(await this.localDb.hasInstalledPack(courseId, pack.subjectId))) {
          await this.ensureSubjectPack(courseId, pack.subjectId);
        }
      }
    }
  }
}
